import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';

export default function CaseRecuperacaoMotoresPneumaticosPage() {
  useSeo({
    title: 'Recuperação de Motores Pneumáticos Industriais | Oficina Especializada | Grupo Pilar',
    description: 'Case de recuperação completa de motores pneumáticos industriais utilizados em paradas gerais do setor de papel e celulose, incluindo desmontagem, substituição de paletas, buchas, rolamentos, vedações e testes operacionais.',
    keywords: 'motor pneumático industrial, manutenção de motor pneumático, reparo de motor pneumático, paletas de celeron, pneumática industrial, parada geral, papel e celulose, oficina especializada, grupo pilar',
    canonical: 'https://www.pilarmanutencao.com.br/cases/recuperacao-motores-pneumaticos-industriais',
    ogUrl: 'https://www.pilarmanutencao.com.br/cases/recuperacao-motores-pneumaticos-industriais',
    ogImage: 'https://readdy.ai/api/search-image?query=Extremely%20realistic%20professional%20industrial%20photography%20modern%20clean%20organized%20heavy%20industrial%20workshop%20with%20large%20machinery%20hydraulic%20equipment%20steel%20components%20metallic%20surfaces%20professional%20technicians%20in%20safety%20gear%20working%20not%20facing%20camera%20cinematic%20dramatic%20lighting%20gray%20silver%20steel%20tones%20with%20subtle%20warm%20yellow%20accent%20details%20wide%20angle%20widescreen%20composition%20premium%20high%20resolution%20sharp%20depth%20industrial%20maintenance%20engineering%20reliability%20heavy%20mechanics%20pulp%20paper%20mining%20oil%20gas%20sectors%20negative%20space%20for%20title%20overlay%20clean%20backgrounds%20not%20stock%20photo%20not%20illustration%20not%203D%20render&width=1600&height=700&seq=gp-cases-unified-hero-v2&orientation=landscape',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroImage = 'https://readdy.ai/api/search-image?query=Extremely%20realistic%20professional%20industrial%20photography%20modern%20clean%20organized%20heavy%20industrial%20workshop%20with%20large%20machinery%20hydraulic%20equipment%20steel%20components%20metallic%20surfaces%20professional%20technicians%20in%20safety%20gear%20working%20not%20facing%20camera%20cinematic%20dramatic%20lighting%20gray%20silver%20steel%20tones%20with%20subtle%20warm%20yellow%20accent%20details%20wide%20angle%20widescreen%20composition%20premium%20high%20resolution%20sharp%20depth%20industrial%20maintenance%20engineering%20reliability%20heavy%20mechanics%20pulp%20paper%20mining%20oil%20gas%20sectors%20negative%20space%20for%20title%20overlay%20clean%20backgrounds%20not%20stock%20photo%20not%20illustration%20not%203D%20render&width=1600&height=700&seq=gp-cases-unified-hero-v2&orientation=landscape';

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt="Motores pneumáticos industriais recuperados pela Pilar — equipamentos desmontados, inspecionados, com paletas de celeron substituídas, rolamentos e vedações trocados, carcaças de alumínio recuperadas, testados e prontos para operação"
              title="Recuperação de Motores Pneumáticos Industriais para Parada Geral — Case Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="inline-block px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider bg-gp-yellow text-gp-graphite">
                  Oficina Especializada
                </span>
                <span className="text-gray-400 text-xs uppercase tracking-wider">Papel e Celulose</span>
                <span className="text-gray-500 text-xs">— Aracruz, ES</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Case:</span>{' '}
                Recuperação de Motores Pneumáticos Industriais para Parada Geral
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
                Recuperação completa e seriada de motores pneumáticos industriais durante paradas gerais do setor
                de papel e celulose, incluindo desmontagem, inspeções técnicas, substituição de paletas de celeron,
                buchas, rolamentos, vedações, recuperação de carcaças em alumínio, jateamento, pintura industrial
                e testes operacionais — até 10 motores por parada geral em prazo de até 7 dias.
              </p>
            </div>
          </div>
        </section>

        {/* Visão Geral */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Visão Geral</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Dados do Projeto</h3>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Equipamento</span>
                        <p className="text-gray-300 text-sm">Motores Pneumáticos Industriais para Equipamentos Críticos de Processo</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-2-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Segmento</span>
                        <p className="text-gray-300 text-sm">Papel e Celulose</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Aplicação</span>
                        <p className="text-gray-300 text-sm">Equipamentos industriais críticos durante operações de processo</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-team-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Volume Típico</span>
                        <p className="text-gray-300 text-sm">Até 10 motores pneumáticos por parada geral — prazo de até 7 dias</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Disciplinas</span>
                        <p className="text-gray-300 text-sm">Mecânica Industrial, Pneumática Industrial, Soldagem Especializada, Inspeção Técnica</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-lightbulb-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Diferencial do Projeto</span>
                        <p className="text-gray-300 text-sm">Capacidade de recuperação seriada de até 10 motores pneumáticos por parada geral com recuperação de carcaças em alumínio</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/20109923-9ba6-4140-a97b-cc1e9e1f146b_compressed_PECAS-MOTOR-PNEUMATICO.webp"
                    alt="Vista geral dos componentes desmontados durante o processo de recuperação do motor pneumático industrial na oficina especializada Pilar"
                    title="Componentes desmontados do motor pneumático — oficina Pilar"
                    className="w-full h-auto object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Vista geral dos componentes desmontados durante o processo de recuperação do motor pneumático.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Desafio */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Desafio</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-alert-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-gray-300 text-base leading-relaxed mb-4">
                        Durante paradas gerais de unidades industriais do setor de papel e celulose, a Pilar executa
                        a recuperação completa de motores pneumáticos utilizados em equipamentos críticos de processo.
                        O serviço é realizado na Oficina Especializada da Pilar, permitindo que os equipamentos retornem
                        à operação com confiabilidade e desempenho adequados para o próximo ciclo operacional.
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        As principais complexidades envolviam:
                      </p>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Grande volume de motores pneumáticos para recuperação em curto prazo de parada geral</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Necessidade de desmontagem completa de cada motor pneumático para inspeção</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Inspeções detalhadas dos componentes internos com verificação de desgastes</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Recuperação estrutural de carcaças em alumínio com correções e reparos</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Substituição de componentes sujeitos a desgaste: paletas, buchas, rolamentos e vedações</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Atendimento aos prazos rigorosos das paradas gerais com recuperação seriada</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/12300852-aabe-4154-90fc-6b343d6bd098_compressed_CARCACA-JATEADA-LIMPA-MOTOR-PNEUMATICO.webp"
                    alt="Carcaça do motor pneumático industrial jateada, limpa e preparada para pintura — etapa de preparação de superfície na oficina especializada Pilar"
                    title="Carcaça jateada e limpa do motor pneumático — preparação para pintura"
                    className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Carcaça do motor pneumático industrial jateada e limpa — superfície preparada para pintura industrial.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Escopo Executado */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Escopo Executado pela Pilar</h2>
              </div>

              <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter mb-8">
                <img
                  src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/5f5ce398-82cf-48af-8237-ca158ed11578_compressed_CONJUNTO-ROTATIVO-MOTOR-PNEUMATICO.webp"
                  alt="Conjunto rotativo do motor pneumático industrial após desmontagem e inspeção — componentes expostos para avaliação técnica na oficina Pilar"
                  title="Conjunto rotativo do motor pneumático industrial"
                  className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                  loading="lazy"
                />
                <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                  <p className="text-gray-400 text-xs text-center">
                    Conjunto rotativo do motor pneumático — componentes expostos para inspeção dimensional e peritagem técnica.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">01</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Desmontagem completa dos motores pneumáticos na oficina especializada Pilar</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">02</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Inspeção dimensional detalhada de todos os componentes internos dos motores pneumáticos</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">03</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Inspeção dos componentes internos com verificação de desgastes, folgas e condições de serviço</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">04</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Limpeza técnica de todos os componentes com remoção de contaminantes, resíduos e oxidações</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">05</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Substituição de buchas conforme diagnóstico técnico das inspeções dimensionais</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">06</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Substituição de rolamentos com seleção conforme especificação técnica do equipamento</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">07</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Substituição de vedações: anéis, retentores, gaxetas e selos pneumáticos dos motores</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">08</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Substituição das paletas de celeron com verificação dimensional e ajuste de folgas operacionais</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">09</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Recuperação de componentes desgastados com recondicionamento técnico e ajustes dimensionais</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">10</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Montagem técnica completa dos motores pneumáticos com controle dimensional e ajustes de precisão</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reparos Especializados */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Reparos Especializados</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-8">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-tools-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Recuperação de Carcaças</h3>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    Um dos diferenciais técnicos deste projeto foi a capacidade de recuperação de carcaças em alumínio,
                    material que exige procedimentos específicos de soldagem e reparo. As carcaças danificadas foram
                    submetidas a correções estruturais e recuperação de alojamentos, devolvendo a integridade dimensional
                    e funcional aos componentes.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Recuperação de carcaças em alumínio com procedimentos específicos de soldagem</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Correções estruturais para restabelecimento da integridade das carcaças</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Reparos em componentes danificados com recondicionamento dimensional</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Recuperação de alojamentos com usinagem e ajustes de precisão</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Ajustes de montagem com verificação de encaixes, folgas e alinhamentos</span>
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/8e4b18ef-3a3a-4c25-9dc9-b52d58aaed67_compressed_INTERNOS-BICROMATIZADOS-MOTOR-PNEUMATICO.webp"
                    alt="Componentes internos do motor pneumático industrial após processo de bicromatização — proteção anticorrosiva aplicada na oficina especializada Pilar"
                    title="Componentes internos bicromatizados do motor pneumático"
                    className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Componentes internos bicromatizados — proteção anticorrosiva aplicada aos componentes do motor pneumático.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Processos Complementares */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Processos Complementares</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 text-center">
                  <div className="w-14 h-14 bg-gp-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-brush-line text-gp-yellow text-2xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Jateamento</h3>
                  <p className="text-gray-400 text-sm">Jateamento industrial para preparação da superfície das carcaças dos motores pneumáticos</p>
                </div>

                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 text-center">
                  <div className="w-14 h-14 bg-gp-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-paint-line text-gp-yellow text-2xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Pintura</h3>
                  <p className="text-gray-400 text-sm">Pintura industrial para proteção anticorrosiva e acabamento dos motores pneumáticos</p>
                </div>

                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 text-center">
                  <div className="w-14 h-14 bg-gp-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-test-tube-line text-gp-yellow text-2xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Testes</h3>
                  <p className="text-gray-400 text-sm">Testes operacionais com validação de desempenho pneumático antes do retorno à operação</p>
                </div>
              </div>

              <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter mt-8">
                <img
                  src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/b0a4f006-22f5-4a84-96a3-4ec84991301c_compressed_POSICIONAMENTO-CONJUNTO-ROTATIVO-MOTOR.webp"
                  alt="Posicionamento do conjunto rotativo durante a montagem do motor pneumático industrial — alinhamento e ajustes na oficina especializada Pilar"
                  title="Posicionamento e montagem do conjunto rotativo do motor pneumático"
                  className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                  loading="lazy"
                />
                <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                  <p className="text-gray-400 text-xs text-center">
                    Posicionamento do conjunto rotativo durante a montagem — alinhamento e ajustes de precisão na montagem do motor pneumático.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Recursos Envolvidos */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Recursos Envolvidos</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 text-center">
                  <div className="w-14 h-14 bg-gp-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-settings-3-line text-gp-yellow text-2xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Mecânica</h3>
                  <p className="text-gray-400 text-sm">Montagem e desmontagem de motores pneumáticos, controle dimensional e ajustes de precisão</p>
                </div>

                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 text-center">
                  <div className="w-14 h-14 bg-gp-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-windy-line text-gp-yellow text-2xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Pneumática</h3>
                  <p className="text-gray-400 text-sm">Conhecimento especializado em sistemas pneumáticos, paletas de celeron e testes operacionais</p>
                </div>

                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 text-center">
                  <div className="w-14 h-14 bg-gp-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-fire-line text-gp-yellow text-2xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Soldagem</h3>
                  <p className="text-gray-400 text-sm">Soldagem especializada em alumínio para recuperação de carcaças, correções estruturais e reparos</p>
                </div>

                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 text-center">
                  <div className="w-14 h-14 bg-gp-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-search-eye-line text-gp-yellow text-2xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Inspeção</h3>
                  <p className="text-gray-400 text-sm">Inspeção dimensional, peritagem técnica de componentes e validação operacional dos motores</p>
                </div>
              </div>

              <div className="mt-8 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <i className="ri-list-check text-gp-yellow w-6 h-6 flex items-center justify-center"></i>
                  <h3 className="font-display text-base font-bold text-white uppercase">Equipe Especializada e Capacidade</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-400 text-sm mb-3">Volume médio de recuperação:</p>
                    <p className="text-gp-yellow text-lg font-semibold">Até 10 motores pneumáticos por parada geral</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-3">Prazo típico:</p>
                    <p className="text-gp-yellow text-lg font-semibold">Até 7 dias para recuperação completa dos equipamentos</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-2 bg-gp-graphite-light border border-gp-graphite-lighter px-4 py-2 rounded-lg text-gray-300 text-sm">
                    <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    Mecânica Industrial
                  </span>
                  <span className="inline-flex items-center gap-2 bg-gp-graphite-light border border-gp-graphite-lighter px-4 py-2 rounded-lg text-gray-300 text-sm">
                    <i className="ri-windy-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    Pneumática Industrial
                  </span>
                  <span className="inline-flex items-center gap-2 bg-gp-graphite-light border border-gp-graphite-lighter px-4 py-2 rounded-lg text-gray-300 text-sm">
                    <i className="ri-fire-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    Soldagem Especializada
                  </span>
                  <span className="inline-flex items-center gap-2 bg-gp-graphite-light border border-gp-graphite-lighter px-4 py-2 rounded-lg text-gray-300 text-sm">
                    <i className="ri-search-eye-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    Inspeção Técnica
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Resultados */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Resultados Obtidos</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="relative rounded-lg overflow-hidden border border-gp-yellow/20 bg-gp-graphite p-6 md:p-8 order-2 lg:order-1">
                  <div className="absolute top-0 left-0 w-1 h-full bg-gp-yellow"></div>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-trophy-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-white uppercase mb-2">Resultados</h3>
                    </div>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-refresh-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Motores Recuperados</span>
                        <p className="text-gray-400 text-sm">Todos os motores pneumáticos recuperados com sucesso e retornados à operação</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Maior Confiabilidade</span>
                        <p className="text-gray-400 text-sm">Confiabilidade operacional significativamente ampliada dos equipamentos de processo</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-alert-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Redução de Falhas</span>
                        <p className="text-gray-400 text-sm">Redução significativa de falhas durante a operação contínua dos equipamentos</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-timer-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Vida Útil Ampliada</span>
                        <p className="text-gray-400 text-sm">Aumento expressivo da vida útil dos motores pneumáticos industriais</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Prazos Cumpridos</span>
                        <p className="text-gray-400 text-sm">Cumprimento dos prazos rigorosos das paradas gerais com recuperação seriada</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg overflow-hidden border border-gp-yellow/20 order-1 lg:order-2">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/788553aa-5b87-446c-b320-fc28c3396a12_compressed_MOTOR-PNEUMATICO-FINALIZADO.webp"
                    alt="Motor pneumático industrial totalmente recuperado, pintado e pronto para retorno à operação — resultado final dos serviços executados na oficina especializada Pilar"
                    title="Motor pneumático recuperado — resultado final"
                    className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Motor pneumático industrial totalmente recuperado — equipamento pintado e pronto para retorno à operação.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Diferenciais da Pilar */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Diferenciais da Pilar</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-stack-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Capacidade de recuperação seriada de motores pneumáticos em volume para atender paradas gerais</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Estrutura de oficina especializada com equipamentos para desmontagem, jateamento, pintura e testes</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Experiência consolidada em paradas gerais industriais do setor de papel e celulose</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-fire-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Recuperação de componentes em alumínio com soldagem especializada e correções estruturais</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-timer-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Atendimento a cronogramas críticos de manutenção com prazos rigorosos de paradas gerais</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Equipe multidisciplinar envolvendo mecânica, pneumática, soldagem especializada e inspeção técnica</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Galeria Técnica */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Galeria Técnica</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/a5fb67fb-dca4-4f40-87cc-d91e24eb8905_compressed_ROLAMENTOS-NOVOS-MOTOR-PNEUMATICO.webp"
                      alt="Rolamentos novos preparados para instalação no motor pneumático industrial durante processo de recuperação na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Rolamentos novos — componentes preparados para substituição</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 1</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/726a072a-9e43-4f91-bcfc-f396cce0eea9_compressed_ROLAMENTOS-MONTADOS-MOTOR-PNEUMATICO.webp"
                      alt="Rolamentos montados no motor pneumático industrial — verificação de ajustes e folgas na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Rolamentos montados — verificação de ajustes e folgas</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 2</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/6d204280-e9eb-4f82-9dff-15c7f989a566_compressed_PALHETAS-CELERON-MOTOR-PNEUMATICO.webp"
                      alt="Palhetas de celeron do motor pneumático industrial — componentes de desgaste substituídos durante a recuperação na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Palhetas de celeron — componentes substituídos</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 3</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/df5590f6-6d8b-4cb1-849b-3ab90546a25a_compressed_MONTAGEM-MOTOR-REDUCAO.webp"
                      alt="Montagem do motor de redução acoplado ao motor pneumático industrial — integração dos sistemas na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem do motor de redução — integração dos sistemas</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 4</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/d431e0fe-3b92-4779-8a25-ba9f0dc9e398_compressed_MONTAGEM-ENGRENAMENTO-MOTOR-PNEUMATICO1.webp"
                      alt="Montagem do engrenamento do motor pneumático industrial — etapa 1 do processo de montagem na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem do engrenamento — etapa 1</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 5</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/004b1e6e-d0c3-46f2-a82d-04277125700d_compressed_MONTAGEM-ENGRENAMENTO-MOTOR-PNEUMATICO2.webp"
                      alt="Montagem do engrenamento do motor pneumático industrial — etapa 2 com ajustes de engrenagens na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem do engrenamento — etapa 2</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 6</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/0b989d0a-bb40-46bd-932b-05cec88201c3_compressed_MONTAGEM-ENGRENAMENTO-MOTOR-PNEUMATICO3.webp"
                      alt="Montagem do engrenamento do motor pneumático industrial — etapa 3 final com verificação de alinhamento na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem do engrenamento — etapa 3 final</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 7</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/ad124823-3aaf-421e-9941-8cf61adbe333_compressed_MONTAGEM-ENGRENAGENS-MOTOR-PNEUMATICO.webp"
                      alt="Montagem do conjunto de engrenagens do motor pneumático industrial na oficina especializada Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem das engrenagens — conjunto completo</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 8</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/037f9bb8-218e-400b-bb61-b80fe0002e1d_compressed_MONTAGEM-ENGRENAGEM-MOTOR-PNEUMATICO.webp"
                      alt="Detalhe da montagem da engrenagem do motor pneumático industrial — ajuste de encaixes na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem da engrenagem — ajuste de encaixes</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 9</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/f3e33408-b21c-4265-ba32-755abcc10736_compressed_MONTAGEM-BUCHA-ROLAMENTO-MOTOR-PNEUMATICO.webp"
                      alt="Montagem da bucha de rolamento no motor pneumático industrial — instalação de componentes na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem da bucha de rolamento</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 10</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/01c453e8-5907-4e4d-80d4-324997255805_compressed_MONTAGEM-ANEL-ELASTICO-MOTOR-PNEUMATICO.webp"
                      alt="Montagem do anel elástico no motor pneumático industrial — fixação de componentes na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem do anel elástico — fixação de componentes</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 11</span>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/9be99318-d95b-4b35-a20f-c164bca29038_compressed_LUBRIFICACAO-ENGRENAGENS-MOTOR-PNEUMATICO.webp"
                      alt="Lubrificação das engrenagens do motor pneumático industrial durante processo de montagem na oficina Pilar"
                      className="w-full h-full object-contain bg-gp-graphite"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Lubrificação das engrenagens</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 12</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Serviços Relacionados */}
        <section className="relative py-16 overflow-hidden border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-lg font-bold text-white uppercase tracking-wider">Serviços Relacionados</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/mecanica-industrial"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mecânica Industrial
                </Link>
                <Link
                  to="/caldeiraria-solda"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Caldeiraria e Solda Industrial
                </Link>
                <Link
                  to="/oficina"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Oficina Especializada
                </Link>
                <Link
                  to="/servicos-de-campo"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Paradas de Manutenção Industrial
                </Link>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mais Cases de Sucesso
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative py-20 bg-gp-graphite-light overflow-hidden">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa de recuperação de motores pneumáticos ou equipamentos industriais para parada geral?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Conte com a estrutura completa da oficina especializada Pilar para recuperação seriada de motores pneumáticos industriais,
                  incluindo desmontagem, inspeções técnicas, substituição de paletas de celeron, buchas, rolamentos e vedações,
                  recuperação de carcaças em alumínio, jateamento, pintura industrial e testes operacionais —
                  atendendo prazos rigorosos de paradas gerais.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://wa.me/5527993092922"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span>Solicitar Orçamento</span>
                    <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <Link
                    to="/contato"
                    className="inline-flex items-center justify-center gap-2 border border-gray-500 hover:border-gp-yellow text-gray-300 hover:text-gp-yellow px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-mail-line text-lg w-6 h-6 flex items-center justify-center"></i>
                    Falar com Especialista
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Cases */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Cases
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />

      {/* Schema JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Recuperação de Motores Pneumáticos Industriais para Parada Geral | Case Grupo Pilar',
            description: 'Case de recuperação completa de motores pneumáticos industriais utilizados em paradas gerais do setor de papel e celulose, incluindo desmontagem, substituição de paletas, buchas, rolamentos, vedações e testes operacionais.',
            image: heroImage,
            author: {
              '@type': 'Organization',
              name: 'Grupo Pilar',
              url: 'https://www.pilarmanutencao.com.br',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Grupo Pilar',
              logo: {
                '@type': 'ImageObject',
                url: '/images/MARCA/LOGO-GRUPO-PILAR/LOGO-GRUPO-PILAR.png',
              },
            },
            datePublished: '2025-12-05',
            dateModified: '2026-06-13',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://www.pilarmanutencao.com.br/cases/recuperacao-motores-pneumaticos-industriais',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://www.pilarmanutencao.com.br',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Cases de Sucesso',
                item: 'https://www.pilarmanutencao.com.br/cases',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Recuperação de Motores Pneumáticos Industriais para Parada Geral',
                item: 'https://www.pilarmanutencao.com.br/cases/recuperacao-motores-pneumaticos-industriais',
              },
            ],
          }),
        }}
      />
    </div>
  );
}